// HomePage/index.jsx — the top-level marketing home page ("/").
// The student-facing page it replaced now lives at "/job-seekers".
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import IntroOverlay, { canPlayIntro } from "./Intro/IntroOverlay";
import Header from "../Home/Header/Header";
import Problem from "./Problem/Problem";
import Solution from "./Solution/Solution";
import Audience from "./Audience/Audience";
import HomeTestimonials from "./Testimonials/HomeTestimonials";
import Articles from "./Articles/Articles";
import GetStarted from "./GetStarted/GetStarted";
import Seo from "../../seo/Seo";
import {
  OrganizationSchema,
  SoftwareApplicationSchema,
} from "../../seo/StructuredData";
import "./homePage.css";

const HomePage = () => {
  // Decided once on mount so a re-render can't restart the curtain.
  const [showIntro, setShowIntro] = useState(canPlayIntro);
  const finishIntro = useCallback(() => setShowIntro(false), []);

  /* Captured on the first render. `showIntro` flips to false when the curtain
     finishes, so keying the replay off it would fire the hero reveal a second
     time on a first visit — the curtain has already played that beat. */
  const introHandledHero = useRef(showIntro);

  /* Belt and braces on the first-paint curtain in public/index.html. It is
     normally lifted by IntroOverlay's own layout effect; this covers the one
     case that cannot reach — the check in the HTML deciding the intro will
     play while canPlayIntro() disagrees, which would leave the white up with
     nothing mounted behind it to clear it. Home is on screen by now either
     way, so there is nothing left for it to hide. */
  useLayoutEffect(() => {
    document.documentElement.classList.remove("intro-pending");
  }, []);

  /* The hero reveal on every arrival the curtain does not cover — the
     headline, subtext, buttons and school logos float up in sequence. This is
     the entrance the career centre, ambassador, about and pricing headers get
     from ui/fade-in.jsx; home is the one page that had nothing, so coming back
     to it from anywhere else dropped the hero in fully formed while every
     other page stepped up.

     It is deliberately only the copy. The opening also fades the tubes canvas
     up behind it ("the colour arrives", see .intro-color) — replaying that
     part on a return is what made a simple navigation look like the whole
     intro starting over, so the canvas is left alone here.

     Hidden in a layout effect and released on the next frame, so the float
     starts from the bottom rather than catching the copy halfway up. */
  useLayoutEffect(() => {
    if (introHandledHero.current) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const root = document.documentElement;
    root.classList.add("hero-replay");

    const frame = requestAnimationFrame(() => root.classList.add("intro-hero"));

    // The longest of these runs 0.8s off a 0.2s delay; drop the classes once
    // it is over so nothing is left holding the hero in an animated state.
    const done = setTimeout(
      () => root.classList.remove("hero-replay", "intro-hero"),
      1600
    );

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(done);
      root.classList.remove("hero-replay", "intro-hero");
    };
  }, []);

  return (
  <div className="home-page">
    <Seo path="/" />
    {/* "RAIN" collides with a fintech, RAIN Group, Raines International and
        Rain AI in search. These two nodes are the clearest signal we can
        send about which RAIN this is. */}
    <SoftwareApplicationSchema />
    <OrganizationSchema />
    {showIntro && <IntroOverlay onFinish={finishIntro} />}
    <Header
      heading="AI-Native Career Readiness Platform"
      subtext="RAIN guides job seekers through career exploration, resumes, networking, interviews, and applications, and gives career centers full visibility along the way."
      ctaLabel="Sign Up"
      bookLabel="Book a Walkthrough"
      logoLabel="Trusted by career centers at"
      showLogoLabel
      wrapHeading
    />
    <Problem />
    <Solution />
    <Audience />
    <HomeTestimonials />
    <Articles />
    <GetStarted />
  </div>
  );
};

export default HomePage;
